/**
 * The landing page — orientation, not summary. It carries no figures, because a figure with no
 * target is not interpretable (§4.6) and every threshold is computed in ADB SQL (§5.2).
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import {
  areaItems,
  countMenuItems,
  menuForRoles,
  type MenuArea,
  type MenuItem,
} from '@/lib/menu';
import { useReportWorkspace } from '@/lib/reportWorkspace';
import { useI18n } from '@/lib/i18n';

export default function OverviewPage() {
  const { text } = useI18n();
  const { user, handoffLoginName } = useAuth();
  const name = user?.name ?? user?.username ?? handoffLoginName;

  const menu = menuForRoles(user?.roles ?? []);
  const total = countMenuItems(menu);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {name === null || name === undefined ? text('Overview') : text('Welcome, {name}', { name })}
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Reports are grouped by area of the business. Pick the area you are asking about and the
          report is under it — inside Production Control, the stages follow the route work takes
          across the floor.
        </p>
      </header>

      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {menu.map((area) => (
          <li key={area.id}>
            <AreaCard area={area} />
          </li>
        ))}
      </ul>

      <p className="text-sm text-muted-foreground">
        {total === 0
          ? 'No reports are ported yet. Each one appears under its area as it lands.'
          : 'Missing a report you used to run? It may not be ported yet — it will appear under its area when it is.'}
      </p>
    </div>
  );
}

function AreaCard({ area }: { area: MenuArea }) {
  const { text } = useI18n();
  const empty = areaItems(area).length === 0;

  return (
    <Card className="h-full gap-4 py-5">
      <CardHeader className="gap-1 px-5">
        {/* A real heading so a screen reader can jump between areas; `CardTitle` renders a div. */}
        <CardTitle>
          <h2 className="flex items-center gap-2.5 text-base">
            <FontAwesomeIcon
              icon={area.icon}
              className="w-4 shrink-0 text-center text-sm text-muted-foreground"
              aria-hidden
            />
            {text(area.label)}
          </h2>
        </CardTitle>
        <CardDescription>{area.description}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 px-5">
        {empty && area.groups.length === 0 && (
          <p className="text-sm text-muted-foreground">{text('No reports yet.')}</p>
        )}

        {area.items.length > 0 && <ReportLinks items={area.items} />}

        {/* Stages stay listed while empty, so the shape of an area is legible before it is full. */}
        {area.groups.map((group) => (
          <div key={group.id} className="space-y-1">
            <h3 className="flex items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
              <FontAwesomeIcon
                icon={group.icon}
                className="w-3.5 shrink-0 text-center"
                aria-hidden
              />
              {text(group.label)}
            </h3>
            {group.items.length === 0 ? (
              <p className="text-xs text-muted-foreground">{text('No reports yet.')}</p>
            ) : (
              <ReportLinks items={group.items} />
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ReportLinks({ items }: { items: readonly MenuItem[] }) {
  const { hrefFor } = useReportWorkspace();
  const { text } = useI18n();

  return (
    <ul className="space-y-1">
      {items.map((item) => (
        <li key={item.reportId}>
          <Link
            to={hrefFor(item.reportId)}
            className="-mx-2 flex min-h-9 items-center rounded-md px-2 text-sm text-foreground outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {text(item.label)}
          </Link>
        </li>
      ))}
    </ul>
  );
}
